<script>
import { useAlert } from 'dashboard/composables';
import SettingsSection from '../../../../../components/SettingsSection.vue';

export default {
  components: {
    SettingsSection,
  },
  props: {
    inbox: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      isLoading: false,
      isSaving: false,
      instanceSettings: {
        reject_call: false,
        groupsIgnore: true,
        alwaysOnline: false,
        readMessages: false,
        readStatus: false,
        syncFullHistory: false,
      },
      // Estados para reconexión
      connectionStatus: 'unknown', // unknown, connected, disconnected
      isCheckingConnection: false,
      showReconnectModal: false,
      qrCode: '',
      isGeneratingQr: false,
      connectionCheckInterval: null,
      reconnectionInterval: null,
      // Estado para restart
      isRestarting: false,
      // Flag para evitar múltiples alertas de reconexión
      hasShownReconnectSuccess: false,
    };
  },
  computed: {
    isEvolutionAPIInbox() {
      // Método 1: Por api_provider (para bandejas nuevas)
      if (this.inbox?.additional_attributes?.api_provider === 'evolution_api') {
        return true;
      }

      // Método 2: Por webhook_url que contenga un patrón Evolution API (para bandejas existentes)
      if (
        this.inbox?.webhook_url &&
        this.inbox.channel_type === 'Channel::Api'
      ) {
        // Si el webhook_url contiene chatwoot/webhook/ es probablemente Evolution API
        return this.inbox.webhook_url.includes('/chatwoot/webhook/');
      }

      return false;
    },
    evolutionApiUrl() {
      return (
        this.inbox?.additional_attributes?.evolution_api_url ||
        window.globalConfig?.EVOLUTION_API_URL
      );
    },
    evolutionApiKey() {
      return (
        this.inbox?.additional_attributes?.evolution_api_key ||
        window.globalConfig?.EVOLUTION_API_KEY
      );
    },
    instanceName() {
      // Prioridad 1: nombre configurado explícitamente
      if (this.inbox?.additional_attributes?.evolution_instance_name) {
        return this.inbox.additional_attributes.evolution_instance_name;
      }

      // Prioridad 2: extraer desde webhook URL
      if (this.inbox?.webhook_url) {
        // Buscar patrón /chatwoot/webhook/{instanceName}
        const webhookMatch = this.inbox.webhook_url.match(
          /\/chatwoot\/webhook\/([^/?]+)/
        );
        if (webhookMatch && webhookMatch[1]) {
          return webhookMatch[1];
        }

        // Fallback: tomar el último segmento de la URL
        const urlParts = this.inbox.webhook_url.split('/');
        const lastPart = urlParts[urlParts.length - 1];
        if (lastPart && lastPart !== 'webhook') {
          return lastPart;
        }
      }

      // Prioridad 3: fallback a name o phone_number
      return this.inbox?.name || this.inbox?.phone_number;
    },
  },
  mounted() {
    if (this.isEvolutionAPIInbox) {
      this.loadInstanceSettings();
      this.checkConnectionStatus();
      // Verificar conexión cada 30 segundos
      this.startConnectionPolling();
    }
  },
  beforeUnmount() {
    this.stopConnectionPolling();
  },
  methods: {
    async loadInstanceSettings() {
      if (
        !this.evolutionApiUrl ||
        !this.evolutionApiKey ||
        !this.instanceName
      ) {
        return;
      }

      this.isLoading = true;
      try {
        const response = await fetch(
          `${this.evolutionApiUrl}/settings/find/${this.instanceName}`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              apikey: this.evolutionApiKey,
            },
          }
        );

        if (!response.ok) {
          throw new Error(
            `Error getting instance settings: ${response.statusText}`
          );
        }

        const result = await response.json();

        // Actualizar las configuraciones con los valores obtenidos
        // Manejar diferentes estructuras de respuesta de la API
        let settings = result.settings || result;

        if (settings) {
          // Helper function para manejar valores booleanos correctamente
          const getValue = (primary, secondary, defaultValue) => {
            if (primary !== undefined) return primary;
            if (secondary !== undefined) return secondary;
            return defaultValue;
          };

          this.instanceSettings = {
            reject_call: getValue(
              settings.reject_call,
              settings.rejectCall,
              false
            ),
            groupsIgnore: getValue(
              settings.groupsIgnore,
              settings.groups_ignore,
              true
            ),
            alwaysOnline: getValue(
              settings.alwaysOnline,
              settings.always_online,
              false
            ),
            readMessages: getValue(
              settings.readMessages,
              settings.read_messages,
              false
            ),
            readStatus: getValue(
              settings.readStatus,
              settings.read_status,
              false
            ),
            syncFullHistory: getValue(
              settings.syncFullHistory,
              settings.sync_full_history,
              false
            ),
          };
        }
      } catch (error) {
        useAlert(
          `${this.$t('INBOX_MGMT.INSTANCE_CONFIGURATION.MESSAGES.LOAD_ERROR')}: ${error.message}`
        );
      } finally {
        this.isLoading = false;
      }
    },

    async saveInstanceSettings() {
      if (
        !this.evolutionApiUrl ||
        !this.evolutionApiKey ||
        !this.instanceName
      ) {
        useAlert(
          this.$t(
            'INBOX_MGMT.INSTANCE_CONFIGURATION.MESSAGES.INCOMPLETE_CONFIG'
          )
        );
        return;
      }

      this.isSaving = true;
      try {
        // Convertir el formato para que coincida con la API de Evolution
        const evolutionApiSettings = {
          rejectCall: this.instanceSettings.reject_call,
          groupsIgnore: this.instanceSettings.groupsIgnore,
          alwaysOnline: this.instanceSettings.alwaysOnline,
          readMessages: this.instanceSettings.readMessages,
          readStatus: this.instanceSettings.readStatus,
          syncFullHistory: this.instanceSettings.syncFullHistory,
        };

        const response = await fetch(
          `${this.evolutionApiUrl}/settings/set/${this.instanceName}`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              apikey: this.evolutionApiKey,
            },
            body: JSON.stringify(evolutionApiSettings),
          }
        );

        if (!response.ok) {
          throw new Error(
            `Error updating instance settings: ${response.statusText}`
          );
        }

        useAlert(
          this.$t('INBOX_MGMT.INSTANCE_CONFIGURATION.MESSAGES.SAVE_SUCCESS')
        );

        // Recargar las configuraciones desde Evolution API para mostrar los valores actualizados
        await this.loadInstanceSettings();
      } catch (error) {
        useAlert(
          `${this.$t('INBOX_MGMT.INSTANCE_CONFIGURATION.MESSAGES.SAVE_ERROR')}: ${error.message}`
        );
      } finally {
        this.isSaving = false;
      }
    },

    // Métodos para verificar y manejar la conexión
    async checkConnectionStatus() {
      if (
        !this.evolutionApiUrl ||
        !this.evolutionApiKey ||
        !this.instanceName
      ) {
        return;
      }

      this.isCheckingConnection = true;
      try {
        const response = await fetch(
          `${this.evolutionApiUrl}/instance/connect/${this.instanceName}`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              apikey: this.evolutionApiKey,
            },
          }
        );

        if (response.ok) {
          const result = await response.json();
          // Verificar diferentes formatos de respuesta
          const isConnected =
            result.instance?.state === 'open' ||
            result.state === 'open' ||
            result.connected === true;

          this.connectionStatus = isConnected ? 'connected' : 'disconnected';
        } else {
          this.connectionStatus = 'disconnected';
        }
      } catch (error) {
        // Error checking connection
        this.connectionStatus = 'disconnected';
      } finally {
        this.isCheckingConnection = false;
      }
    },

    async restartInstance() {
      if (
        !this.evolutionApiUrl ||
        !this.evolutionApiKey ||
        !this.instanceName
      ) {
        // Error: Configuración incompleta para reiniciar instancia
        useAlert(
          this.$t(
            'INBOX_MGMT.INSTANCE_CONFIGURATION.MESSAGES.INCOMPLETE_CONFIG'
          )
        );
        return;
      }

      // Iniciando reinicio de instancia
      this.isRestarting = true;

      try {
        const response = await fetch(
          `${this.evolutionApiUrl}/instance/restart/${this.instanceName}`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              apikey: this.evolutionApiKey,
            },
          }
        );

        if (response.ok) {
          await response.json();
          // Instancia reiniciada exitosamente

          useAlert(
            this.$t(
              'INBOX_MGMT.INSTANCE_CONFIGURATION.MESSAGES.RESTART_SUCCESS'
            )
          );

          // Verificar el estado de conexión después de un breve delay
          setTimeout(() => {
            this.checkConnectionStatus();
          }, 2000);
        } else if (response.status === 404) {
          // Error 404: Instancia no encontrada
          useAlert(
            this.$t(
              'INBOX_MGMT.INSTANCE_CONFIGURATION.MESSAGES.INSTANCE_NOT_FOUND'
            )
          );
        } else {
          const errorText = await response.text();
          // Error en reinicio de instancia
          throw new Error(`HTTP ${response.status}: ${errorText}`);
        }
      } catch (error) {
        // Error al reiniciar instancia
        useAlert(
          `${this.$t('INBOX_MGMT.INSTANCE_CONFIGURATION.MESSAGES.RESTART_ERROR')}: ${error.message}`
        );
      } finally {
        this.isRestarting = false;
      }
    },

    startConnectionPolling() {
      this.connectionCheckInterval = setInterval(() => {
        this.checkConnectionStatus();
      }, 30000); // Verificar cada 30 segundos
    },

    stopConnectionPolling() {
      if (this.connectionCheckInterval) {
        clearInterval(this.connectionCheckInterval);
        this.connectionCheckInterval = null;
      }
    },

    async startReconnection() {
      this.hasShownReconnectSuccess = false; // Reset flag
      this.showReconnectModal = true;
      await this.generateQrCode();
    },

    async generateQrCode() {
      this.isGeneratingQr = true;
      this.qrCode = '';

      try {
        // Intentar múltiples endpoints para generar QR
        let result = null;

        // Endpoint principal
        try {
          const response = await fetch(
            `${this.evolutionApiUrl}/instance/connect/${this.instanceName}`,
            {
              method: 'GET',
              headers: {
                'Content-Type': 'application/json',
                apikey: this.evolutionApiKey,
              },
            }
          );

          if (response.ok) {
            result = await response.json();
          }
        } catch (error) {
          // Intentar endpoint alternativo
          const response2 = await fetch(
            `${this.evolutionApiUrl}/instance/qr/${this.instanceName}`,
            {
              method: 'GET',
              headers: {
                'Content-Type': 'application/json',
                apikey: this.evolutionApiKey,
              },
            }
          );

          if (response2.ok) {
            result = await response2.json();
          } else {
            throw new Error(`Error generating QR: ${response2.statusText}`);
          }
        }

        if (!result) {
          throw new Error('No response from Evolution API');
        }

        // Verificar diferentes posibles estructuras de respuesta
        let qrData = null;

        // Buscar el QR en diferentes campos posibles
        if (result.base64) {
          qrData = result.base64;
        } else if (result.code) {
          qrData = result.code;
        } else if (result.qrcode) {
          qrData = result.qrcode;
        } else if (result.qr) {
          qrData = result.qr;
        } else if (result.data) {
          qrData = result.data;
        } else if (result.qr_code) {
          qrData = result.qr_code;
        } else if (result.image) {
          qrData = result.image;
        }

        if (qrData) {
          // Limpiar el string QR de caracteres innecesarios
          qrData = qrData.toString().trim();

          // Si ya viene con el prefijo data:image, usarlo tal como está
          if (qrData.startsWith('data:image/')) {
            this.qrCode = qrData;
          } else if (
            qrData.startsWith('iVBOR') ||
            qrData.startsWith('/9j/') ||
            qrData.match(/^[A-Za-z0-9+/]+=*$/)
          ) {
            // Si parece ser base64 válido
            this.qrCode = `data:image/png;base64,${qrData}`;
          } else {
            // Como último recurso, intentar como URL directa
            this.qrCode = qrData;
          }

          this.startReconnectionPolling();
        } else {
          throw new Error('QR code data not found in API response');
        }
      } catch (error) {
        useAlert(`Error generating QR code: ${error.message}`);
        this.closeReconnectModal();
      } finally {
        this.isGeneratingQr = false;
      }
    },

    startReconnectionPolling() {
      // Verificar conexión cada 3 segundos durante la reconexión
      this.reconnectionInterval = setInterval(async () => {
        await this.checkConnectionStatus();
        if (this.connectionStatus === 'connected') {
          this.onReconnectionSuccess();
        }
      }, 3000);
    },

    stopReconnectionPolling() {
      if (this.reconnectionInterval) {
        clearInterval(this.reconnectionInterval);
        this.reconnectionInterval = null;
      }
    },

    onReconnectionSuccess() {
      if (!this.hasShownReconnectSuccess) {
        this.hasShownReconnectSuccess = true;
        useAlert(
          this.$t(
            'INBOX_MGMT.INSTANCE_CONFIGURATION.MESSAGES.RECONNECT_SUCCESS'
          )
        );
      }
      this.stopReconnectionPolling();
      this.closeReconnectModal();
    },

    closeReconnectModal() {
      this.showReconnectModal = false;
      this.qrCode = '';
      this.isGeneratingQr = false;
      this.stopReconnectionPolling();
    },

    async refreshQrCode() {
      await this.generateQrCode();
    },

    onQrImageError() {
      // Error loading QR image
      useAlert('Error loading QR code image. Please try refreshing.');
    },

    onQrImageLoad() {
      // QR code cargado correctamente
    },
  },
};
</script>

<template>
  <div v-if="isEvolutionAPIInbox">
    <SettingsSection
      :title="$t('INBOX_MGMT.INSTANCE_CONFIGURATION.TITLE')"
      :sub-title="$t('INBOX_MGMT.INSTANCE_CONFIGURATION.DESCRIPTION')"
    >
      <div v-if="isLoading" class="flex justify-center py-8">
        <div
          class="animate-spin rounded-full h-8 w-8 border-b-2 border-woot-500"
        />
      </div>

      <div v-else class="space-y-6">
        <!-- Estado de Conexión -->
        <div class="config-section">
          <div class="flex items-center space-x-3">
            <div class="flex-shrink-0">
              <div
                v-if="connectionStatus === 'connected'"
                class="connection-indicator connection-indicator--connected"
              />
              <div
                v-else-if="connectionStatus === 'disconnected'"
                class="connection-indicator connection-indicator--disconnected"
              />
              <div
                v-else
                class="connection-indicator connection-indicator--checking"
              />
            </div>
            <div>
              <h4 class="config-title">
                {{
                  connectionStatus === 'connected'
                    ? $t(
                        'INBOX_MGMT.INSTANCE_CONFIGURATION.CONNECTION_STATUS.CONNECTED'
                      )
                    : connectionStatus === 'disconnected'
                      ? $t(
                          'INBOX_MGMT.INSTANCE_CONFIGURATION.CONNECTION_STATUS.DISCONNECTED'
                        )
                      : $t(
                          'INBOX_MGMT.INSTANCE_CONFIGURATION.CONNECTION_STATUS.CHECKING'
                        )
                }}
              </h4>
              <p v-if="connectionStatus === 'disconnected'" class="error-text">
                {{
                  $t(
                    'INBOX_MGMT.INSTANCE_CONFIGURATION.CONNECTION_STATUS.NEEDS_RECONNECTION'
                  )
                }}
              </p>
            </div>
          </div>
          <div class="flex items-center space-x-2">
            <button
              v-if="connectionStatus === 'disconnected'"
              type="button"
              class="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
              @click="startReconnection"
            >
              {{
                $t(
                  'INBOX_MGMT.INSTANCE_CONFIGURATION.CONNECTION_STATUS.RECONNECT'
                )
              }}
            </button>
            <button
              type="button"
              class="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-woot-500 hover:bg-woot-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-woot-500 disabled:opacity-50"
              :disabled="isCheckingConnection"
              @click="checkConnectionStatus"
            >
              <div
                v-if="isCheckingConnection"
                class="spinner spinner--small mr-1"
              />
              {{
                $t(
                  'INBOX_MGMT.INSTANCE_CONFIGURATION.CONNECTION_STATUS.CHECK_STATUS'
                )
              }}
            </button>
            <button
              v-if="connectionStatus === 'connected'"
              type="button"
              class="inline-flex items-center px-4 py-2 text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm border border-transparent"
              :disabled="isRestarting"
              @click="restartInstance"
            >
              <div v-if="isRestarting" class="spinner spinner--small mr-2" />
              {{
                $t(
                  'INBOX_MGMT.INSTANCE_CONFIGURATION.CONNECTION_STATUS.RESTART'
                )
              }}
            </button>
          </div>
        </div>

        <!-- Sección de reconexión QR (aparece debajo del estado) -->
        <div v-if="showReconnectModal" class="qr-section">
          <h3 class="qr-title">
            {{
              $t(
                'INBOX_MGMT.INSTANCE_CONFIGURATION.CONNECTION_STATUS.RECONNECT'
              )
            }}
          </h3>

          <!-- Estado: Generando QR -->
          <div v-if="isGeneratingQr" class="text-center py-8">
            <div class="spinner spinner--xl mx-auto mb-4" />
            <p class="generating-text">
              {{
                $t('INBOX_MGMT.INSTANCE_CONFIGURATION.MESSAGES.GENERATING_QR')
              }}
            </p>
          </div>

          <!-- QR Code -->
          <div v-else-if="qrCode" class="text-center">
            <div class="mb-6">
              <img
                :src="qrCode"
                alt="QR Code"
                class="qr-image"
                @error="onQrImageError"
                @load="onQrImageLoad"
              />
            </div>
            <p class="qr-scan-text">
              {{ $t('INBOX_MGMT.INSTANCE_CONFIGURATION.MESSAGES.SCAN_QR') }}
            </p>
            <div class="qr-instructions">
              <p>
                {{
                  $t('INBOX_MGMT.INSTANCE_CONFIGURATION.QR_INSTRUCTIONS.STEP_1')
                }}
              </p>
              <p>
                {{
                  $t('INBOX_MGMT.INSTANCE_CONFIGURATION.QR_INSTRUCTIONS.STEP_2')
                }}
              </p>
              <p>
                {{
                  $t('INBOX_MGMT.INSTANCE_CONFIGURATION.QR_INSTRUCTIONS.STEP_3')
                }}
              </p>
              <p>
                {{
                  $t('INBOX_MGMT.INSTANCE_CONFIGURATION.QR_INSTRUCTIONS.STEP_4')
                }}
              </p>
            </div>

            <!-- Botones de acción -->
            <div class="flex justify-center space-x-3">
              <button
                type="button"
                class="inline-flex items-center px-4 py-2 border border-blue-300 text-sm font-medium rounded-md text-blue-700 bg-white hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                @click="refreshQrCode"
              >
                {{
                  $t(
                    'INBOX_MGMT.INSTANCE_CONFIGURATION.CONNECTION_STATUS.REFRESH_QR'
                  )
                }}
              </button>
              <button
                type="button"
                class="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                @click="closeReconnectModal"
              >
                {{
                  $t(
                    'INBOX_MGMT.INSTANCE_CONFIGURATION.CONNECTION_STATUS.CANCEL'
                  )
                }}
              </button>
            </div>
          </div>
        </div>

        <!-- Configuraciones de instancia (solo se muestran cuando NO está en modo reconexión) -->
        <template v-else>
          <!-- Reject Calls -->
          <div class="config-section">
            <div class="flex-1">
              <h4 class="config-title">
                {{ $t('INBOX_MGMT.INSTANCE_CONFIGURATION.REJECT_CALLS.TITLE') }}
              </h4>
              <p class="config-description">
                {{
                  $t(
                    'INBOX_MGMT.INSTANCE_CONFIGURATION.REJECT_CALLS.DESCRIPTION'
                  )
                }}
              </p>
            </div>
            <label class="relative inline-flex items-center cursor-pointer">
              <input
                v-model="instanceSettings.reject_call"
                type="checkbox"
                class="sr-only peer"
              />
              <div
                class="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-woot-500"
              />
            </label>
          </div>

          <!-- Groups Ignore -->
          <div class="config-section">
            <div class="flex-1">
              <h4 class="config-title">
                {{
                  $t('INBOX_MGMT.INSTANCE_CONFIGURATION.GROUPS_IGNORE.TITLE')
                }}
              </h4>
              <p class="config-description">
                {{
                  $t(
                    'INBOX_MGMT.INSTANCE_CONFIGURATION.GROUPS_IGNORE.DESCRIPTION'
                  )
                }}
              </p>
            </div>
            <label class="relative inline-flex items-center cursor-pointer">
              <input
                v-model="instanceSettings.groupsIgnore"
                type="checkbox"
                class="sr-only peer"
              />
              <div
                class="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-woot-500"
              />
            </label>
          </div>

          <!-- Always Online -->
          <div class="config-section">
            <div class="flex-1">
              <h4 class="config-title">
                {{
                  $t('INBOX_MGMT.INSTANCE_CONFIGURATION.ALWAYS_ONLINE.TITLE')
                }}
              </h4>
              <p class="config-description">
                {{
                  $t(
                    'INBOX_MGMT.INSTANCE_CONFIGURATION.ALWAYS_ONLINE.DESCRIPTION'
                  )
                }}
              </p>
            </div>
            <label class="relative inline-flex items-center cursor-pointer">
              <input
                v-model="instanceSettings.alwaysOnline"
                type="checkbox"
                class="sr-only peer"
              />
              <div
                class="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-woot-500"
              />
            </label>
          </div>

          <!-- Read Messages -->
          <div class="config-section">
            <div class="flex-1">
              <h4 class="config-title">
                {{
                  $t('INBOX_MGMT.INSTANCE_CONFIGURATION.READ_MESSAGES.TITLE')
                }}
              </h4>
              <p class="config-description">
                {{
                  $t(
                    'INBOX_MGMT.INSTANCE_CONFIGURATION.READ_MESSAGES.DESCRIPTION'
                  )
                }}
              </p>
            </div>
            <label class="relative inline-flex items-center cursor-pointer">
              <input
                v-model="instanceSettings.readMessages"
                type="checkbox"
                class="sr-only peer"
              />
              <div
                class="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-woot-500"
              />
            </label>
          </div>

          <!-- Read Status -->
          <div class="config-section">
            <div class="flex-1">
              <h4 class="config-title">
                {{ $t('INBOX_MGMT.INSTANCE_CONFIGURATION.READ_STATUS.TITLE') }}
              </h4>
              <p class="config-description">
                {{
                  $t(
                    'INBOX_MGMT.INSTANCE_CONFIGURATION.READ_STATUS.DESCRIPTION'
                  )
                }}
              </p>
            </div>
            <label class="relative inline-flex items-center cursor-pointer">
              <input
                v-model="instanceSettings.readStatus"
                type="checkbox"
                class="sr-only peer"
              />
              <div
                class="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-woot-500"
              />
            </label>
          </div>

          <!-- Sync Full History -->
          <div class="config-section">
            <div class="flex-1">
              <h4 class="config-title">
                {{
                  $t(
                    'INBOX_MGMT.INSTANCE_CONFIGURATION.SYNC_FULL_HISTORY.TITLE'
                  )
                }}
              </h4>
              <p class="config-description">
                {{
                  $t(
                    'INBOX_MGMT.INSTANCE_CONFIGURATION.SYNC_FULL_HISTORY.DESCRIPTION'
                  )
                }}
              </p>
            </div>
            <label class="relative inline-flex items-center cursor-pointer">
              <input
                v-model="instanceSettings.syncFullHistory"
                type="checkbox"
                class="sr-only peer"
              />
              <div
                class="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-woot-500"
              />
            </label>
          </div>

          <!-- Save Button -->
          <div class="pt-6 border-t border-gray-200">
            <button
              type="button"
              class="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-woot-500 hover:bg-woot-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-woot-500 disabled:opacity-50"
              :disabled="isSaving"
              @click="saveInstanceSettings"
            >
              <div v-if="isSaving" class="spinner spinner--medium mr-2" />
              {{
                isSaving
                  ? $t('INBOX_MGMT.INSTANCE_CONFIGURATION.SAVING')
                  : $t('INBOX_MGMT.INSTANCE_CONFIGURATION.SAVE')
              }}
            </button>
          </div>
        </template>
      </div>
    </SettingsSection>
  </div>

  <!-- Fallback for non-Evolution API inboxes
  - informacion -->
  <div v-else>
    <SettingsSection
      :title="$t('INBOX_MGMT.INSTANCE_CONFIGURATION.TITLE')"
      :sub-title="$t('INBOX_MGMT.INSTANCE_CONFIGURATION.NOT_AVAILABLE')"
    >
      <div class="text-center py-8">
        <p class="fallback-text">
          {{ $t('INBOX_MGMT.INSTANCE_CONFIGURATION.ONLY_EVOLUTION_API') }}
        </p>
      </div>
    </SettingsSection>
  </div>
</template>
